/**
 * Based on immutability-helper
 * https://github.com/kolodny/immutability-helper/blob/3dc903960b8411da84704052d511c20648c45ead/index.ts#L85-L142
 */

function stringifiable(obj) {
  // Safely stringify Object.create(null)
  return typeof obj === 'object' && !('toString' in obj) ? Object.prototype.toString.call(obj).slice(8, -1) : obj;
}
const isProduction = typeof process === 'object' && process.env.NODE_ENV === 'production';
export function invariant(condition, message) {
  if (!condition) {
    if (isProduction) {
      throw new Error('Invariant failed');
    }
    throw new Error(message());
  }
}
const hasOwnProperty = Object.prototype.hasOwnProperty;
const splice = Array.prototype.splice;
const toString = Object.prototype.toString;
function type(obj) {
  return toString.call(obj).slice(8, -1);
}
const getAllKeys = typeof Object.getOwnPropertySymbols === 'function' ? obj => Object.keys(obj).concat(Object.getOwnPropertySymbols(obj)) : obj => Object.keys(obj);
const assign = Object.assign || ((target, source) => {
  // @ts-expect-error Argument of type 'S' is not assignable to parameter of type 'Object'
  getAllKeys(source).forEach(key => {
    if (hasOwnProperty.call(source, key)) {
      // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      target[key] = source[key];
    }
  });
  return target;
});
function copy(object) {
  return Array.isArray(object) ? assign(object.constructor(object.length), object) : type(object) === 'Map' ? new Map(object) : type(object) === 'Set' ? new Set(object) : object && typeof object === 'object' ? assign(Object.create(Object.getPrototypeOf(object)), object) : /* istanbul ignore next */
  object;
}
const defaultCommands = {
  $push(value, nextObject, spec) {
    invariantPushAndUnshift(nextObject, spec, '$push');
    return value.length ? nextObject.concat(value) : nextObject;
  },
  $unshift(value, nextObject, spec) {
    invariantPushAndUnshift(nextObject, spec, '$unshift');
    return value.length ? value.concat(nextObject) : nextObject;
  },
  $splice(value, nextObject, spec, originalObject) {
    invariantSplices(nextObject, spec);
    value.forEach(args => {
      invariantSplice(args);
      if (nextObject === originalObject && args.length) {
        nextObject = copy(originalObject);
      }
      splice.apply(nextObject, args);
    });
    return nextObject;
  },
  $set(value, _nextObject, spec) {
    invariantSet(spec);
    return value;
  },
  $toggle(targets, nextObject) {
    invariantSpecArray(targets, '$toggle');
    const nextObjectCopy = targets.length ? copy(nextObject) : nextObject;
    targets.forEach(target => {
      nextObjectCopy[target] = !nextObject[target];
    });
    return nextObjectCopy;
  },
  $unset(value, nextObject, _spec, originalObject) {
    invariantSpecArray(value, '$unset');
    value.forEach(key => {
      if (Object.hasOwnProperty.call(nextObject, key)) {
        if (nextObject === originalObject) {
          nextObject = copy(originalObject);
        }
        delete nextObject[key];
      }
    });
    return nextObject;
  },
  $add(values, nextObject, _spec, originalObject) {
    invariantMapOrSet(nextObject, '$add');
    invariantSpecArray(values, '$add');
    if (type(nextObject) === 'Map') {
      values.forEach(([key, value]) => {
        if (nextObject === originalObject && nextObject.get(key) !== value) {
          nextObject = copy(originalObject);
        }
        nextObject.set(key, value);
      });
    } else {
      values.forEach(value => {
        if (nextObject === originalObject && !nextObject.has(value)) {
          nextObject = copy(originalObject);
        }
        nextObject.add(value);
      });
    }
    return nextObject;
  },
  $remove(value, nextObject, _spec, originalObject) {
    invariantMapOrSet(nextObject, '$remove');
    invariantSpecArray(value, '$remove');
    value.forEach(key => {
      if (nextObject === originalObject && nextObject.has(key)) {
        nextObject = copy(originalObject);
      }
      nextObject.delete(key);
    });
    return nextObject;
  },
  $merge(value, nextObject, _spec, originalObject) {
    invariantMerge(nextObject, value);
    getAllKeys(value).forEach(key => {
      if (value[key] !== nextObject[key]) {
        if (nextObject === originalObject) {
          nextObject = copy(originalObject);
        }
        nextObject[key] = value[key];
      }
    });
    return nextObject;
  },
  $apply(value, original) {
    invariantApply(value);
    return value(original);
  }
};
export class Context {
  constructor() {
    this.commands = assign({}, defaultCommands);
    this.update = this.update.bind(this);
    // Deprecated: update.extend, update.isEquals and update.newContext
    this.update.extend = this.extend = this.extend.bind(this);
    this.update.isEquals = (x, y) => x === y;
    this.update.newContext = () => new Context().update;
  }
  get isEquals() {
    return this.update.isEquals;
  }
  set isEquals(value) {
    this.update.isEquals = value;
  }
  extend(directive, fn) {
    // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    this.commands[directive] = fn;
  }
  update(object, $spec) {
    const spec = typeof $spec === 'function' ? {
      $apply: $spec
    } : $spec;
    if (!(Array.isArray(object) && Array.isArray(spec))) {
      invariant(!Array.isArray(spec), () => `update(): You provided an invalid spec to update(). The spec may ` + `not contain an array except as the value of $set, $push, $unshift, ` + `$splice or any custom command allowing an array value.`);
    }
    invariant(typeof spec === 'object' && spec !== null, () => `update(): You provided an invalid spec to update(). The spec and ` + `every included key path must be plain objects containing one of the ` + `following commands: ${Object.keys(this.commands).join(', ')}.`);
    let nextObject = object;
    getAllKeys(spec).forEach(key => {
      if (hasOwnProperty.call(this.commands, key)) {
        const objectWasNextObject = object === nextObject;
        nextObject = this.commands[key](spec[key], nextObject, spec, object);
        if (objectWasNextObject && this.isEquals(nextObject, object)) {
          nextObject = object;
        }
      } else {
        const nextValueForKey = type(object) === 'Map' ? this.update(object.get(key), spec[key]) : this.update(object[key], spec[key]);
        const nextObjectValue = type(nextObject) === 'Map' ? nextObject.get(key) : nextObject[key];
        if (!this.isEquals(nextValueForKey, nextObjectValue) || typeof nextValueForKey === 'undefined' && !hasOwnProperty.call(object, key)) {
          if (nextObject === object) {
            nextObject = copy(object);
          }
          if (type(nextObject) === 'Map') {
            nextObject.set(key, nextValueForKey);
          } else {
            nextObject[key] = nextValueForKey;
          }
        }
      }
    });
    return nextObject;
  }
}
const defaultContext = new Context();
export const isEquals = defaultContext.update.isEquals;
export const extend = defaultContext.extend;
export default defaultContext.update;

// invariants
function invariantPushAndUnshift(value, spec, command) {
  invariant(Array.isArray(value), () => `update(): expected target of ${stringifiable(command)} to be an array; got ${stringifiable(value)}.`);
  invariantSpecArray(spec[command], command);
}
function invariantSpecArray(spec, command) {
  invariant(Array.isArray(spec), () => `update(): expected spec of ${stringifiable(command)} to be an array; got ${stringifiable(spec)}. ` + `Did you forget to wrap your parameter in an array?`);
}
function invariantSplices(value, spec) {
  invariant(Array.isArray(value), () => `Expected $splice target to be an array; got ${stringifiable(value)}`);
  invariantSplice(spec.$splice);
}
function invariantSplice(value) {
  invariant(Array.isArray(value), () => `update(): expected spec of $splice to be an array of arrays; got ${stringifiable(value)}. ` + `Did you forget to wrap your parameters in an array?`);
}
function invariantApply(fn) {
  invariant(typeof fn === 'function', () => `update(): expected spec of $apply to be a function; got ${stringifiable(fn)}.`);
}
function invariantSet(spec) {
  invariant(Object.keys(spec).length === 1, () => `Cannot have more than one key in an object with $set`);
}
function invariantMerge(target, specValue) {
  invariant(specValue && typeof specValue === 'object', () => `update(): $merge expects a spec of type 'object'; got ${stringifiable(specValue)}`);
  invariant(target && typeof target === 'object', () => `update(): $merge expects a target of type 'object'; got ${stringifiable(target)}`);
}
function invariantMapOrSet(target, command) {
  const typeOfTarget = type(target);
  invariant(typeOfTarget === 'Map' || typeOfTarget === 'Set', () => `update(): ${stringifiable(command)} expects a target of type Set or Map; got ${stringifiable(typeOfTarget)}`);
}

/**
 * Usage with custom commands is as follows:
 *
 *   interface MyCommands {
 *     $foo: string;
 *   }
 *
 *   update<Foo, CustomCommands<MyCommands>>(..., { $foo: "bar" });
 *
 * It is suggested that if you use custom commands frequently, you wrap and
 * re-export a properly-typed version of `update`:
 *
 *   function myUpdate<T>(object: T, spec: Spec<T, CustomCommands<MyCommands>>) {
 *     return update(object, spec);
 *   }
 *
 * See https://github.com/kolodny/immutability-helper/pull/108 for explanation
 * of why this type exists.
 */

// Note that this does not type check properly if index: number.